import { useState } from 'react'
import { Icon } from '@iconify/react'
import { useGlobalContext } from '../../../../../utils/Context'
import { payInvoice } from '../../../../../utils/WeightDiscrepancyApi'
import Modal from '../../../../../components/modal/Modal'
import FormLoader from '../../../../../components/loader/FormLoader'

interface PaymentModalProps {
  invoiceId: string
  amount: number
  closeModal: () => void
  onSuccess: () => void
}

function PaymentModal({ invoiceId, amount, closeModal, onSuccess }: PaymentModalProps) {
  const { notify, wallet } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const [method, setMethod] = useState<'wallet' | 'credit'>('wallet')

  const walletBalance = wallet?.ngn_balance || 0
  const canPayWithWallet = walletBalance >= amount

  const handlePayment = () => {
    setLoading(true)
    payInvoice(invoiceId, method)
      .then((result: any) => {
        if (result.status === 200) {
          notify('success', 'Invoice paid successfully')
          onSuccess()
          closeModal()
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch((error: any) => {
        notify('error', error?.message || 'Payment failed')
        setLoading(false)
      })
  }

  const isDisabled = loading || (method === 'wallet' && !canPayWithWallet)

  const MethodCard = ({
    id, icon, title, subtitle,
  }: { id: 'wallet' | 'credit'; icon: string; title: string; subtitle: string }) => {
    const selected = method === id
    return (
      <div
        className={`wd-pay-method-card${selected ? ' selected' : ''}`}
        onClick={() => setMethod(id)}
      >
        <div className='wd-pm-left'>
          <div className={`wd-pm-icon${selected ? ' selected' : ''}`}>
            <Icon icon={icon} width='20' color={selected ? 'white' : '#6b7280'} />
          </div>
          <div>
            <p className='wd-pm-title'>{title}</p>
            <p className='wd-pm-sub'>{subtitle}</p>
          </div>
        </div>
        <div className={`wd-pm-radio${selected ? ' selected' : ''}`}>
          {selected && <Icon icon='mdi:check' width='12' color='white' />}
        </div>
      </div>
    )
  }

  return (
    <Modal position='center'>
      <div className='wd-pay-modal'>
        {/* Header */}
        <div className='wd-pay-header'>
          <div className='wd-pay-header-left'>
            <div className='wd-pay-header-icon'>
              <Icon icon='mdi:credit-card-outline' width='20' color='#0056b3' />
            </div>
            <h2>Pay Invoice</h2>
          </div>
          <button className='wd-pay-close' onClick={closeModal}>
            <Icon icon='mdi:close' width='18' color='#6b7280' />
          </button>
        </div>

        {/* Amount banner */}
        <div className='wd-pay-amount-banner'>
          <p className='wd-pay-amount-label'>Amount to Pay</p>
          <p className='wd-pay-amount-value'>₦{amount.toLocaleString()}</p>
        </div>

        {/* Methods */}
        <div className='wd-pay-methods'>
          <span className='wd-pay-methods-label'>Select Payment Method</span>

          <MethodCard
            id='wallet'
            icon='ion:wallet-outline'
            title='Business Wallet'
            subtitle={`Balance: ₦${walletBalance.toLocaleString()}`}
          />

          {method === 'wallet' && !canPayWithWallet && (
            <div className='wd-pay-error'>
              <Icon icon='mdi:alert-circle-outline' width='16' />
              Insufficient wallet balance
            </div>
          )}

          <MethodCard
            id='credit'
            icon='mdi:bank-outline'
            title='Credit Limit'
            subtitle='Pay later · Added to debt batch'
          />
        </div>

        {/* Actions */}
        <div className='wd-pay-actions'>
          <button className='wd-pay-cancel' onClick={closeModal}>
            Cancel
          </button>
          <button
            className='wd-pay-confirm'
            onClick={handlePayment}
            disabled={isDisabled}
          >
            {loading ? (
              <FormLoader />
            ) : (
              <>
                <Icon icon='mdi:lock-check-outline' width='17' />
                Confirm Payment
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PaymentModal
